import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { defer, EMPTY, Observable, of } from 'rxjs';
import { catchError, mergeMap, share, single, tap } from 'rxjs/operators';

import { AuthenticationDataService, AuthenticationService } from 'app/core/authentication';
import { UrlService } from 'app/core/http/url.service';
import { DefaultPageRouteService } from 'app/core/navigation';
import { GameUserHeaderCreator } from '../login-utility/login-header-creator.service';

const GAME_USER_ITEM_NAME = 'createdGameUserResult';
/**
 * All HTTP GameServer calls must pass through this.
 *
 * This allows strong pre-conditions for subsequent
 * calls to game server. i.e. Such as ensuring the user has been created in the game.
 */
@Injectable({
  providedIn: 'root'
})
export class GameServerGateway {
  private readonly urlService = inject(UrlService);
  private readonly gameHeaderService = inject(GameUserHeaderCreator);
  private readonly http = inject(HttpClient);
  private readonly authenticationDataService = inject(AuthenticationDataService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly defaultPageRouteService = inject(DefaultPageRouteService);

  private _userCreation$?: Observable<string | null> | undefined = undefined;

  // Avoids duplicate log outs when parallel calls see no token.
  private _missingTokenExitScheduled = false;

  public constructor() {
    localStorage.removeItem(GAME_USER_ITEM_NAME);
  }

  // PUBLIC

  public get<T>(url: string, params?: Record<string, string>): Observable<T> {
    return this.ensureGameUserExists().pipe(mergeMap(() => this.whenAccessTokenPresent(() => {
      const headers: HttpHeaders = this.gameHeaderService.getHTTPHeaders();
      return this.http.get<T>(url, { headers, params });
    })));
  }

  public put<T>(url: string, body: unknown, customOptions?: Record<string, unknown>): Observable<T> {
    return this.ensureGameUserExists().pipe(mergeMap(() => this.whenAccessTokenPresent(() =>
      this.http.put<T>(url, body, this.buildOptions(customOptions))
    )));
  }

  public post<T>(url: string, body: unknown, customOptions?: Record<string, unknown>): Observable<T> {
    return this.ensureGameUserExists().pipe(mergeMap(() => this.whenAccessTokenPresent(() =>
      this.http.post<T>(url, body, this.buildOptions(customOptions))
    )));
  }

  public patch<T>(url: string, body: unknown, customOptions?: Record<string, unknown>): Observable<T> {
    return this.ensureGameUserExists().pipe(mergeMap(() => this.whenAccessTokenPresent(() =>
      this.http.patch<T>(url, body, this.buildOptions(customOptions))
    )));
  }

  public delete<T>(url: string, body: unknown, customOptions?: Record<string, unknown>): Observable<T> {
    return this.ensureGameUserExists().pipe(mergeMap(() => this.whenAccessTokenPresent(() => {
      const options: object = this.buildOptions({ ...customOptions, body: body });
      return this.http.delete<T>(url, options);
    })));
  }

  /**
   * Ensures the game user exists and we have an access token before making any game API calls.
   */
  public ensureGameUserExists(): Observable<string | null> {
    return this.whenAccessTokenPresent(() => this.ensureGameUserExistsCore());
  }

  private ensureGameUserExistsCore(): Observable<string | null> {
    if (this.isSureGameUserCreated()) {
      return this.getStoredCreationResult();
    }

    if (this._userCreation$) {
      return this._userCreation$;
    }

    return this.makeCreationCall();
  }

  public isSureGameUserCreated(): boolean {
    const userId: string | null = this.getResult();
    if (!userId) {
      return false;
    }

    const isSameUserId: boolean = this.isUserIdsEqual(userId, this.gameHeaderService.getUserId());
    if (!isSameUserId) {
      localStorage.removeItem(GAME_USER_ITEM_NAME);
      this._userCreation$ = undefined;
      return false;
    }
    return true;
  }

  // PRIVATE

  /**
   * Mirrors RequestInterceptor.redirectOnUnauthorized: avoids server 401 spam when the client has no bearer token.
   */
  private redirectOnUnauthorized(): void {
    this.authenticationService.logOut();
    this.defaultPageRouteService.navigateToPublicFallback().catch((error: unknown) => console.error(error));
  }

  /**
   * Checks if the user has an access token and if not, redirects to the public fallback page.
   */
  private whenAccessTokenPresent<T>(work: () => Observable<T>): Observable<T> {
    return defer(() => {
      if (this.authenticationDataService.isExistAccessToken()) {
        this._missingTokenExitScheduled = false;
        return work();
      }

      if (!this._missingTokenExitScheduled) {
        this._missingTokenExitScheduled = true;
        this.redirectOnUnauthorized();
      }

      return EMPTY;
    });
  }

  private isUserIdsEqual(userId1: string, userId2: string): boolean {
    if (this.isValidUserId(userId1) && this.isValidUserId(userId2)) {
      return userId1.toUpperCase() === userId2.toUpperCase();
    }
    throw new Error('Both strings must be non-empty.');
  }

  /**
   * pre: Expects the user is already be created.
   * @returns - The user ID.
   */
  private getStoredCreationResult(): Observable<string | null> {
    const storedUserId: string | null = this.getResult();
    if (!storedUserId || !this.isValidUserId(storedUserId)) {
      this.throwInvalidUserIdError(storedUserId);
    }

    return of(storedUserId);
  }

  private makeCreationCall(): Observable<string | null> {
    return this.createCall()
      .pipe(
        tap((value) => {
          if (!this.isValidUserId(value)) {
            this.throwInvalidUserIdError(value);
          }
          this.storeResult(value);
        }),
        catchError((e: HttpErrorResponse) => {
          console.error(e.message);

          return EMPTY;
        }));
  }

  private throwInvalidUserIdError(invalidId: unknown): void {
    throw new Error(`Expected stored userId to be a non-empty string without spaces. It was ${String(invalidId)}.`);
  }

  private isValidUserId(id: string | null): boolean {
    return typeof id === 'string' && !!id && /(.|\s)*\S(.|\s)*/.test(id);
  }

  private createCall(): Observable<string | null> {
    const url: string = this.urlService.buildGameServerApiUrl('users', 'create-game-user');
    if (!this._userCreation$) {
      this._userCreation$ = this.http.post<string>(url, {}, this.buildOptions({ responseType: 'text' })).pipe(single(), share());
    }

    return this._userCreation$ ? this._userCreation$ : of(null);
  }

  private getResult(): string | null {
    return localStorage.getItem(GAME_USER_ITEM_NAME);
  }

  private storeResult(result: string | null): void {
    if (!result || !this.isValidUserId(result)) {
      throw new Error(`null result or userId ${String(result)} is not a valid userId.`);
    }
    localStorage.setItem(GAME_USER_ITEM_NAME, result);
  }

  private buildOptions(customOptions?: object): object {
    const headers: HttpHeaders = this.gameHeaderService.getHTTPHeaders();

    return { ...customOptions, headers: headers };
  }
}
